import { pic } from "@/lib/responsive-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Search, Sparkles, Phone, ArrowRight } from "lucide-react";
import { getPublishedPosts, type BlogCategory } from "@/lib/blog";
import { BlogCard } from "@/components/BlogCard";
import { useI18n } from "@/lib/i18n";
import { useHeroFade } from "@/hooks/use-hero-fade";
import { cn } from "@/lib/utils";
import { WaveDivider } from "@/components/WaveDivider";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
const blogHero = pic("blog-hero");
const blogCta = pic("blog-cta");

const CATEGORIES: BlogCategory[] = [
  "roofing-tips",
  "solar-energy",
  "roof-repairs",
  "storm-damage",
  "energy-efficiency",
  "residential-roofing",
  "commercial-roofing",
];

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog — Roofing & Solar Insights | AMR 3 New Mexico" },
      {
        name: "description",
        content:
          "Expert roofing and solar advice for New Mexico homeowners. Tips, repair guides, energy savings, storm damage and more from AMR 3 Design & Construction.",
      },
      { property: "og:title", content: "AMR 3 Blog — Roofing & Solar Insights" },
      {
        property: "og:description",
        content: "Expert roofing and solar advice for New Mexico homeowners.",
      },
      { property: "og:url", content: "https://www.amr3designconstruction.com/blog" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://www.amr3designconstruction.com/og-blog.jpg" },
      { property: "og:image:secure_url", content: "https://www.amr3designconstruction.com/og-blog.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "AMR 3 Blog — Roofing & Solar Insights" },
      { name: "twitter:image", content: "https://www.amr3designconstruction.com/og-blog.jpg" },
      { name: "twitter:title", content: "AMR 3 Blog — Roofing & Solar Insights" },
      { name: "twitter:description", content: "Expert roofing and solar advice for New Mexico homeowners." },
    ],
    links: [{ rel: "canonical", href: "https://www.amr3designconstruction.com/blog" }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const { t, lang } = useI18n();
  const heroRef = useHeroFade<HTMLElement>();
  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog", "all"],
    queryFn: () => getPublishedPosts(),
    staleTime: 1000 * 60 * 5,
  });

  const [query, setQuery] = useState("");
  const [active, setActive] = useState<BlogCategory | "all">("all");

  const filtered = useMemo(() => {
    if (!posts) return [];
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (active !== "all" && p.category !== active) return false;
      if (!q) return true;
      const title = (lang === "es" ? p.title_es : p.title_en).toLowerCase();
      const excerpt = (lang === "es" ? p.excerpt_es : p.excerpt_en).toLowerCase();
      return title.includes(q) || excerpt.includes(q);
    });
  }, [posts, query, active, lang]);

  return (
    <div>
      <section ref={heroRef} className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28 will-change-[opacity]">
        <img
          src={blogHero.src}
          srcSet={blogHero.srcSet}
          sizes="100vw"
          alt="Laptop with roofing and solar articles beside blueprints and shingle samples in front of a modern New Mexico home"
          width={1920}
          height={1080}
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
        />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(120deg, oklch(0.18 0.03 50 / 0.80) 0%, oklch(0.18 0.03 50 / 0.58) 55%, oklch(0.18 0.03 50 / 0.34) 100%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Breadcrumbs items={[{ label: t("nav.home"), to: "/" }, { label: t("nav.blog") }]} />
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-background/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-accent backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> {t("blog.eyebrow")}
            </span>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-tight text-background sm:text-5xl lg:text-6xl">
              {t("blog.indexTitle")}
            </h1>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
              <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
                <a href="tel:+15058680847" className="gap-2">
                  <Phone className="h-4 w-4" /> {t("common.callPhone")}
                </a>
              </Button>
              <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
                <Link to="/contact" hash="project-form">{t("common.requestConsultation")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col gap-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("blog.searchPlaceholder")}
                className="w-full rounded-full border border-border bg-card py-3 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <FilterPill active={active === "all"} onClick={() => setActive("all")}>
                {t("blog.cat.all")}
              </FilterPill>
              {CATEGORIES.map((c) => (
                <FilterPill key={c} active={active === c} onClick={() => setActive(c)}>
                  {t(`blog.cat.${c}`)}
                </FilterPill>
              ))}
            </div>
          </div>

          <div className="mb-8 text-center">
            <h2 className="font-serif text-2xl font-bold text-primary sm:text-3xl">
              {lang === "es" ? "Artículos recientes" : "Latest Articles"}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              {lang === "es"
                ? "Guías prácticas escritas por nuestro equipo con más de 25 años de experiencia: cómo detectar daños en el techo, cuándo reparar o reemplazar, cómo funcionan los reclamos de seguro por granizo y cuánto puede ahorrar con energía solar en Nuevo México."
                : "Practical guides written by our team with 25+ years in the field: how to spot roof damage, when to repair versus replace, how hail insurance claims work, and how much solar can save you in New Mexico."}
            </p>
          </div>

          {isLoading ? (
            <p className="py-16 text-center text-muted-foreground">{t("blog.loading")}</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground">{t("blog.empty")}</p>
              <Link
                to="/blog"
                onClick={() => {
                  setQuery("");
                  setActive("all");
                }}
                className="mt-4 inline-block text-sm font-semibold text-accent-strong hover:underline"
              >
                {t("blog.clearFilters")}
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p) => (
                <BlogCard key={p.id} post={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA — image-backed */}
      <section className="relative isolate overflow-hidden py-20 sm:py-28">
        <img
          src={blogCta.src}
          srcSet={blogCta.srcSet}
          sizes="100vw"
          alt="Homeowner reviewing a roofing and solar guide on a tablet beside blueprints and shingle samples at sunset"
          width={1920}
          height={1080}
          loading="lazy"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
        />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(110deg, oklch(0.18 0.03 50 / 0.82) 0%, oklch(0.18 0.03 50 / 0.63) 50%, oklch(0.18 0.03 50 / 0.38) 100%)",
          }}
        />
        <div className="absolute left-0 right-0 top-0 z-10">
          <WaveDivider flip color="var(--background)" />
        </div>
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-accent/40 bg-background/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-accent backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> {t("blog.cta.badge")}
          </div>
          <h2 className="mt-5 font-serif text-3xl font-bold leading-tight text-background sm:text-4xl lg:text-5xl">
            {t("blog.cta.title")}
          </h2>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild variant="gold" size="xl" className="hover-pulse-gold w-full sm:w-auto">
              <a href="tel:+15058680847">
                <Phone className="h-4 w-4" /> {t("common.callPhone")}
              </a>
            </Button>
            <Button asChild variant="whiteOutline" size="xl" className="w-full sm:w-auto">
              <Link to="/contact" hash="project-form">
                {t("common.requestConsultation")} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors",
        active
          ? "border-accent bg-accent text-accent-foreground"
          : "border-border bg-background text-primary hover:border-accent hover:text-accent",
      )}
    >
      {children}
    </button>
  );
}
